import { Link } from 'react-router-dom';
import { DecisionHelper } from '../components/DecisionHelper';

const questions = [
  {
    question: 'Wie wichtig ist dir völlige Gestaltungsfreiheit bei Grundriss und Raumaufteilung?',
    options: [
      { text: 'Sehr wichtig – ich möchte alles genau nach meinen Vorstellungen planen', weights: [2, 0] },
      { text: 'Wichtig, aber ich kann auch mit einem guten Bestand arbeiten', weights: [1, 1] },
      { text: 'Weniger wichtig – Charakter und Geschichte eines Hauses gefallen mir', weights: [0, 2] }
    ]
  },
  {
    question: 'Wie ist dein zeitlicher Rahmen für das Projekt?',
    options: [
      { text: 'Ich habe Zeit und kann 18-24 Monate auf den Einzug warten', weights: [2, 0] },
      { text: 'Ich möchte in 12 Monaten einziehen', weights: [1, 1] },
      { text: 'Je schneller, desto besser – idealerweise in 6 Monaten', weights: [0, 2] }
    ]
  },
  {
    question: 'Wie stehst du zu energetischer Effizienz und Nachhaltigkeit?',
    options: [
      { text: 'Höchste Priorität – ich möchte neueste Standards (Klimahaus A oder besser)', weights: [2, 0] },
      { text: 'Wichtig, aber auch ein gut sanierter Altbau kann effizient sein', weights: [1, 2] },
      { text: 'Sekundär – andere Faktoren sind mir wichtiger', weights: [0, 1] }
    ]
  },
  {
    question: 'Welches Budget hast du für das Gesamtprojekt eingeplant?',
    options: [
      { text: 'Über 600.000 € – ich möchte keine Kompromisse eingehen', weights: [2, 0] },
      { text: '400.000 – 600.000 € – ein gutes Preis-Leistungs-Verhältnis ist wichtig', weights: [1, 1] },
      { text: 'Unter 400.000 € – ich muss aufs Budget achten', weights: [0, 2] }
    ]
  },
  {
    question: 'Wie wichtig ist dir eine gewachsene Nachbarschaft und Infrastruktur?',
    options: [
      { text: 'Weniger wichtig – ein Neubaugebiet am Ortsrand ist okay', weights: [2, 0] },
      { text: 'Beides hat Vorteile – ich bin flexibel', weights: [1, 1] },
      { text: 'Sehr wichtig – ich möchte in einem etablierten Viertel wohnen', weights: [0, 2] }
    ]
  },
  {
    question: 'Wie gehst du mit Überraschungen und Unvorhergesehenem um?',
    options: [
      { text: 'Kein Problem – bei einem Neubau habe ich alles unter Kontrolle', weights: [2, 0] },
      { text: 'Ich kann damit umgehen, wenn der Rahmen stimmt', weights: [1, 1] },
      { text: 'Ich rechne damit – bei alten Häusern gibt es immer Überraschungen', weights: [0, 2] }
    ]
  }
];

const outcomes = [
  {
    title: 'Neubau',
    icon: 'home',
    description: 'Basierend auf deinen Antworten passt ein Neubau besser zu dir. Du schätzt Gestaltungsfreiheit, moderne Standards und bist bereit, etwas länger auf dein Traumhaus zu warten.',
    pros: [
      'Volle Gestaltungsfreiheit bei Grundriss und Ausstattung',
      'Neueste energetische Standards (Klimahaus A, Photovoltaik)',
      'Keine versteckten Mängel oder Sanierungskosten',
      'Moderne Haustechnik von Anfang an'
    ],
    cta: {
      title: 'Den richtigen Baupartner finden',
      text: 'Ein erfahrener Baupartner ist entscheidend für den Erfolg deines Neubauprojekts.',
      link: {
        url: 'https://hafner-ec.com',
        label: 'hafner-ec.com besuchen'
      }
    }
  },
  {
    title: 'Sanierung',
    icon: 'hammer',
    description: 'Eine Sanierung scheint besser zu dir zu passen. Du schätzt Charakter, bevorzugst gewachsene Strukturen und möchtest vielleicht schneller einziehen.',
    pros: [
      'Oft bessere Lage in gewachsenen Vierteln',
      'Charakter und Geschichte des Gebäudes',
      'Schnellerer Einzug möglich',
      'Häufig günstigerer Gesamtpreis'
    ],
    cta: {
      title: 'Sanierung richtig planen',
      text: 'Eine professionelle Sanierung braucht Erfahrung. Informiere dich über die wichtigsten Schritte.',
      link: {
        url: 'https://sanieren.it',
        label: 'sanieren.it besuchen'
      }
    }
  }
];

const relatedLinks = [
  { path: '/haus-und-bau/was-kostet-eine-sanierung', label: 'Was kostet eine Sanierung?' },
  { path: '/ratgeber/hausbau-in-suedtirol', label: 'Hausbau in Südtirol' },
  { path: '/tippspiele/was-kostet-dieses-haus', label: 'Was kostet dieses Haus?' }
];

export default function NeubauOderSanierung() {
  return (
    <div className="section-spacing" data-testid="neubau-sanierung-page">
      <div className="container-wide">
        {/* Breadcrumb */}
        <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0284C7]">Start</Link>
          <span className="mx-2">/</span>
          <Link to="/tippspiele" className="hover:text-[#0284C7]">Tippspiele</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0F172A]">Neubau oder Sanierung?</span>
        </nav>

        <DecisionHelper
          title="Neubau oder Sanierung?"
          introText="Beantworte 6 Fragen und finde heraus, welche Option besser zu deiner Situation passt. Es gibt kein Richtig oder Falsch – nur das, was zu dir passt."
          questions={questions}
          outcomes={outcomes}
          relatedLinks={relatedLinks}
        />

        {/* SEO Content */}
        <div className="mt-16 advisor-content">
          <h2>Die große Entscheidung: Neu bauen oder sanieren?</h2>
          <p>
            Diese Frage stellen sich viele Bauherren in Südtirol. Beide Optionen haben 
            ihre Berechtigung – die richtige Wahl hängt von deiner persönlichen Situation, 
            deinen Prioritäten und deinem Budget ab.
          </p>
          
          <h3>Wann lohnt sich ein Neubau?</h3>
          <p>
            Ein Neubau bietet maximale Gestaltungsfreiheit und moderne Energiestandards. 
            Allerdings brauchst du ein passendes Grundstück – und die sind in Südtirol 
            rar und teuer. Plane mit einer Bauzeit von 12-24 Monaten.
          </p>

          <h3>Wann ist Sanierung die bessere Wahl?</h3>
          <p>
            Sanierung lohnt sich besonders bei guter Bausubstanz und attraktiver Lage. 
            Oft bekommst du für weniger Geld mehr Wohnfläche – musst aber mit 
            Überraschungen rechnen. Ein erfahrener Partner wie{' '}
            <a href="https://sanieren.it" target="_blank" rel="noopener noreferrer">
              sanieren.it
            </a>{' '}
            hilft dir, die Kosten realistisch einzuschätzen.
          </p>

          <div className="cta-box">
            <div className="cta-box-title">Du hast dich entschieden?</div>
            <p className="cta-box-text">
              Egal ob Neubau oder Sanierung – der nächste Schritt ist die 
              Kostenplanung. Unser Schätzspiel gibt dir ein Gefühl für realistische Budgets.
            </p>
            <Link to="/haus-und-bau/was-kostet-eine-sanierung" className="text-[#0284C7] font-medium hover:underline">
              Was kostet eine Sanierung? →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
